import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  toastAutoHideState,
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../../utils/atoms/atoms";
import {
  getCostingParameters,
  getCostingQuantity,
  getCostingHistory,
  saveCostingRun,
  getCostingRmRates,
  getCostingFormats,
} from "../../api/costing";
import {
  CostingHistoryEntry,
  CostingItem,
  Operation,
} from "../../interfaces/app_types";
import CostingForm from "./CostingForm";
import StagingPanel from "./StagingPanel";
import HistoryBarCosting from "./HistoryBarCosting";
import LaserCosting from "./LaserCosting";
import FabricNesting from "./FabricNesting";
import { costingFormValidationSchema } from "../../utils/validations/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import ToastNotification from "../common/ToastNotification";
import {
  addOperationsToItems,
  moveCostingItem,
  prepareCostingItem,
} from "./costingItems";

const Costing = () => {
  const [costingMode, setCostingMode] = useState<
    "regular" | "laser" | "fabric"
  >("regular");
  const INITIAL_ITEM: CostingItem = {
    part_name: "",
    drawing_no: "",
    length: "",
    breadth: "",
    thickness: "",
    is_pipe: false,
    raw_material_rate: 69,
    laser_rate: "",
    quantity: 10,
    operations: [],
    tools: [],
    assembly: [],
    lineItems: [],
  };
  const [stagedItems, setStagedItems] = useState<CostingItem[]>([]);
  const [costingTitle, setCostingTitle] = useState("");
  const [costingFormat, setCostingFormat] = useState("Basic");
  const [activeHistoryId, setActiveHistoryId] = useState<string | null>(null);
  const [editingPosition, setEditingPosition] = useState<number | null>(null);

  const queryClient = useQueryClient();

  // ── Toast ─────────────────────────────────────────────────────────────────

  const [toastBg, setToastBg] = useRecoilState(toastBgState);
  const [toastMessage, setToastMessage] = useRecoilState(toastMessageState);
  const [toastAutoHide, setAutoHide] = useRecoilState(toastAutoHideState);
  const setToastShow = useSetRecoilState(toastShowState);

  const showToast = (bg: string, message: string) => {
    setToastBg(bg);
    setToastMessage(message);
    setToastShow(true);
  };

  const resolver = yupResolver(costingFormValidationSchema);

  const form = useForm<CostingItem>({ defaultValues: INITIAL_ITEM, resolver });
  const {
    reset,
    formState: { errors },
  } = form;

  const {
    data: predefinedOps = [],
    isLoading: opsLoading,
    isError: opsError,
  } = useQuery({
    queryKey: ["costing_parameters"],
    queryFn: getCostingParameters,
  });

  const {
    data: predefinedQt = [],
    isLoading: qtsLoading,
    isError: qtsError,
  } = useQuery({
    queryKey: ["costing_quantity"],
    queryFn: getCostingQuantity,
  });

  const {
    data: predefinedRt = [],
    isLoading: rtsLoading,
    isError: rtsError,
  } = useQuery({
    queryKey: ["costing_rmrates"],
    queryFn: getCostingRmRates,
  });

  const {
    data: predefinedFts = [],
    isLoading: ftsLoading,
    isError: ftsErrors,
  } = useQuery({
    queryKey: ["costing_formats"],
    queryFn: getCostingFormats,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["costing_history"],
    queryFn: getCostingHistory,
  });

  const { mutate: commitRun, isLoading: isSaving } = useMutation({
    mutationFn: saveCostingRun,
    onSuccess: () => {
      showToast("success", "Costing run exported successfully!");
      setStagedItems([]);
      setCostingTitle("");
      setActiveHistoryId(null);
      queryClient.invalidateQueries(["costing_history"]);
    },
    onError: (err: any) => {
      showToast(
        "error",
        err?.response?.data?.error ?? "Server error — could not save run."
      );
    },
  });

  // ── Helpers ───────────────────────────────────────────────────────────────

  const stripEmptyFields = (item: CostingItem): CostingItem => {
    const clean = { ...item };
    if (!clean.laser_rate) delete clean.laser_rate;
    if (!clean.operations?.length) delete clean.operations;
    if (!clean.tools?.length) delete clean.tools;
    return clean;
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const stageItem = (data: CostingItem) => {
    const cleanedItem = stripEmptyFields(data);

    // if new item added
    if (editingPosition !== null) {
      setStagedItems((prev) => {
        const updated = [...prev];
        updated.splice(editingPosition, 0, cleanedItem);
        return updated;
      });
      setEditingPosition(null);
    }
    // if item being edited
    else {
      setStagedItems((prev) => [...prev, cleanedItem]);
    }

    reset(INITIAL_ITEM);
    showToast("success", "Component staged successfully!");
  };

  const editStagedItem = (index: number) => {
    const item = stagedItems[index];
    setEditingPosition(index);
    reset(prepareCostingItem(item));
    setStagedItems((prev) => prev.filter((_, i) => i !== index));
    showToast("success", "Item loaded into editor.");
  };

  const cloneItem = (item: CostingItem) => {
    setEditingPosition(null);
    reset({
      ...prepareCostingItem(item),
      drawing_no: `${item.drawing_no}_REV1`,
    });
    showToast("success", "Item cloned into editor.");
  };

  const removeStagedItem = (index: number) => {
    const item = stagedItems[index];

    // Check assemblies across all other staged items
    const assembledDrawingNos = new Set(
      stagedItems
        .reduce<string[]>(
          (acc, s) => acc.concat((s.assembly ?? []).map((a) => a.drawing_no)),
          []
        )
        .filter(Boolean)
    );

    // Also check the current form if user is mid-edit
    (form.getValues("assembly") ?? [])
      .map((a) => a.drawing_no)
      .filter(Boolean)
      .forEach((d) => assembledDrawingNos.add(d));

    if (assembledDrawingNos.has(item.drawing_no)) {
      showToast("danger", "Part of assembly");
      return;
    }

    setStagedItems((prev) => prev.filter((_, i) => i !== index));
    setEditingPosition((position) =>
      position !== null && index < position ? position - 1 : position
    );
  };

  const reorderStagedItem = (from: number, to: number) => {
    if (
      from === to ||
      from < 0 ||
      to < 0 ||
      from >= stagedItems.length ||
      to >= stagedItems.length
    )
      return;
    setStagedItems((prev) => moveCostingItem(prev, from, to));
    // Keep the editor's insertion point aligned when cards move across it.
    setEditingPosition((position) => {
      if (position === null) return null;
      const afterRemoval = position - (from < position ? 1 : 0);
      return afterRemoval + (to < afterRemoval ? 1 : 0);
    });
  };

  const openHistoryEntry = (entry: CostingHistoryEntry) => {
    setEditingPosition(null);
    if (activeHistoryId === entry.id) {
      setActiveHistoryId(null);
      setStagedItems([]);
      setCostingTitle("");
      reset(INITIAL_ITEM);
      return;
    }

    const itemsWithLineItems = entry.items.map(prepareCostingItem);

    setActiveHistoryId(entry.id);
    setStagedItems(itemsWithLineItems);
    setCostingTitle(entry.title);
    reset(INITIAL_ITEM);
    showToast("success", `Loaded "${entry.title}"`);
  };

  const handleCommit = () => {
    if (!costingTitle.trim()) {
      showToast("danger", "Please enter a title for this costing.");
      return;
    }
    commitRun({
      title: costingTitle.trim(),
      format: costingFormat,
      id: activeHistoryId ?? undefined,
      items: stagedItems,
    });
  };

  const stageBulkItems = (items: CostingItem[]) => {
    const cleanedItems = items.map(stripEmptyFields);
    setStagedItems((prev) => [...prev, ...cleanedItems]);
    reset(INITIAL_ITEM);

    showToast(
      "success",
      `${cleanedItems.length} components staged successfully!`
    );
  };

  const applyBulkOperations = (indices: number[], operations: Operation[]) => {
    if (isSaving || !operations.length || !indices.length) return;
    setStagedItems((prev) => addOperationsToItems(prev, indices, operations));
    showToast(
      "success",
      `Operations added to ${indices.length} component${
        indices.length === 1 ? "" : "s"
      }.`
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="costing-page">
      <div
        className="costing-mode-switch"
        role="group"
        aria-label="Costing type"
      >
        <button
          type="button"
          className={`costing-mode-switch__button ${
            costingMode === "regular" ? "is-active" : ""
          }`}
          aria-pressed={costingMode === "regular"}
          onClick={() => setCostingMode("regular")}
        >
          Regular costing
        </button>
        <button
          type="button"
          className={`costing-mode-switch__button ${
            costingMode === "laser" ? "is-active" : ""
          }`}
          aria-pressed={costingMode === "laser"}
          onClick={() => setCostingMode("laser")}
        >
          Laser cutting costing
        </button>
        <button
          type="button"
          className={`costing-mode-switch__button ${
            costingMode === "fabric" ? "is-active" : ""
          }`}
          aria-pressed={costingMode === "fabric"}
          onClick={() => setCostingMode("fabric")}
        >
          Fabric nesting
        </button>
      </div>
      <div hidden={costingMode !== "fabric"}>
        <FabricNesting />
      </div>
      <div hidden={costingMode !== "laser"}>
        <LaserCosting
          predefinedQts={predefinedQt}
          predefinedRts={predefinedRt}
          optionsLoading={qtsLoading || rtsLoading}
          optionsError={qtsError || rtsError}
        />
      </div>
      <div hidden={costingMode !== "regular"}>
        <HistoryBarCosting
          history={history}
          activeHistoryId={activeHistoryId}
          onEntryClick={openHistoryEntry}
        />

        <div className="costing-title-bar">
          <div className="costing-title-bar__input-wrap d-flex align-items-end justify-content-between">
            <div className="">
              <label className="costing-field__label">COSTING TITLE</label>
              <input
                className="costing-input costing-title-bar__input"
                placeholder="COSTING"
                value={costingTitle}
                onChange={(e) => setCostingTitle(e.target.value)}
              />
            </div>

            <div>
              {ftsLoading && <p className="costing-hint">Loading formats...</p>}
              {ftsErrors && (
                <p className="costing-hint costing-hint--error">
                  Failed to load formats
                </p>
              )}
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="costing-formats">Format</label>
                <select
                  id="costing-formats"
                  className="costing-input form-select"
                  value={costingFormat}
                  onChange={(e) => setCostingFormat(e.target.value)}
                >
                  {predefinedFts.map((field) => (
                    <option key={field.id} value={field.value}>
                      {field.value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="costing-grid">
          <CostingForm
            form={form}
            stagedItems={stagedItems}
            predefinedOps={predefinedOps}
            predefinedQts={predefinedQt}
            predefinedRts={predefinedRt}
            opsLoading={opsLoading}
            qtLoading={qtsLoading}
            rtLoading={rtsLoading}
            opsError={opsError}
            qtError={qtsError}
            rtError={rtsLoading}
            onStage={stageItem}
            onBulkStage={stageBulkItems}
          />

          <StagingPanel
            predefinedOps={predefinedOps}
            opsLoading={opsLoading}
            opsError={opsError}
            onApplyOperations={applyBulkOperations}
            stagedItems={stagedItems}
            activeHistoryId={activeHistoryId}
            isSaving={isSaving}
            onEdit={editStagedItem}
            onClone={cloneItem}
            onRemove={removeStagedItem}
            onReorder={reorderStagedItem}
            onCommit={handleCommit}
          />
        </div>
      </div>
      <ToastNotification
        bg={toastBg}
        message={toastMessage}
        autohide={toastAutoHide}
      />
    </div>
  );
};

export default Costing;
